import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Flex, Text } from "rebass/styled-components";
import styled from "styled-components";
import { TbClockX } from "react-icons/tb";
import { IoCheckmarkCircle } from "react-icons/io5";

import { analytics } from "MVVM/Analytics";

export const OutdatedMatchInterestModal = ({
  matchId,
  onClose,
  matchPlayedAt,
}: {
  matchId: string;
  onClose: () => void;
  matchPlayedAt?: string;
}) => {
  const [submitted, setSubmitted] = useState(false);

  const daysExpired = matchPlayedAt
    ? Math.floor(
        (Date.now() - new Date(matchPlayedAt).getTime()) /
          (1000 * 60 * 60 * 24),
      )
    : undefined;

  useEffect(() => {
    analytics.track("Fake Door - View", {
      location: "matchHistory",
      locationIdentifier: "outdatedMatchInterest",
      source: "website",
      matchId,
      daysExpired,
    });
  }, [matchId, daysExpired]);

  const handleInterestClick = () => {
    analytics.track("Fake Door - Click", {
      location: "matchHistory",
      locationIdentifier: "outdatedMatchInterest",
      source: "website",
      matchId,
      daysExpired,
    });
    setSubmitted(true);
  };

  return createPortal(
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        {submitted ? (
          <>
            <IconContainer>
              <StyledCheckIcon />
            </IconContainer>
            <Text
              variant="text.headingMedium"
              fontWeight={700}
              textAlign="center"
            >
              Thanks for your interest!
            </Text>
            <Text
              variant="text.paragraphBase"
              color="chalk"
              textAlign="center"
              fontWeight={500}
            >
              We're still working on this feature and will let you know when
              it's available.
            </Text>
            <DismissButton onClick={onClose}>Close</DismissButton>
          </>
        ) : (
          <>
            <IconContainer>
              <StyledClockIcon />
            </IconContainer>
            <Text
              variant="text.headingMedium"
              fontWeight={700}
              textAlign="center"
            >
              Want to revisit this match?
            </Text>
            <Text
              variant="text.paragraphBase"
              color="chalk"
              textAlign="center"
              fontWeight={500}
            >
              This match has expired, but we're exploring the ability to create
              clips from older matches.
            </Text>
            <InterestButton onClick={handleInterestClick}>
              I'm interested
            </InterestButton>
            <DismissButton onClick={onClose}>No thanks</DismissButton>
          </>
        )}
      </ModalContainer>
    </Overlay>,
    document.getElementById("root") || document.body,
  );
};

const Overlay = styled(Flex)`
  align-items: center;
  background: rgba(0, 0, 0, 0.75);
  inset: 0;
  justify-content: center;
  position: fixed;
  z-index: 9999;
`;

const ModalContainer = styled(Flex)`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.modalBg};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  border-radius: 20px;
  box-shadow: 0 0 30px ${({ theme }) => theme.colors.darkerNed};
  flex-direction: column;
  gap: 16px;
  max-width: 420px;
  padding: 40px 32px 32px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    margin: 0 16px;
    padding: 32px 20px 24px;
  }
`;

const IconContainer = styled(Flex)`
  align-items: center;
  background: ${({ theme }) => theme.colors.chalkAlpha05};
  border-radius: 16px;
  height: 64px;
  justify-content: center;
  width: 64px;
`;

const StyledClockIcon = styled(TbClockX)`
  color: ${({ theme }) => theme.colors.error};
  height: 32px;
  width: 32px;
`;

const StyledCheckIcon = styled(IoCheckmarkCircle)`
  color: ${({ theme }) => theme.colors.envy};
  height: 32px;
  width: 32px;
`;

const InterestButton = styled.button`
  background: ${({ theme }) => theme.colors.envy};
  border: none;
  border-radius: 10px;
  color: white;
  cursor: pointer;
  font-family: Barlow;
  font-size: 15px;
  font-weight: 700;
  margin-top: 8px;
  padding: 12px 24px;
  transition: opacity 0.15s ease;
  width: 100%;

  &:hover {
    opacity: 0.9;
  }
`;

const DismissButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
  padding: 4px;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;
